import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import StepRowCard from "../components/stepRowCard";
import StepContainer from "../stepContainer";
import AddButton from "../components/addButton";
import StepDrawer from "../stepDrawer";
import { GRID_NAMES } from "../../../constants/gridNames";
import { API_PARAM } from "../../../constants/apiParams";
import DetailsDialogs from "../components/detailsDialogs";
import { saveStep } from "../../../services/profileService";
import { calculateAdvancedQuery } from "../utils/calculateAdvancedQuery";
import { profileActions } from "../../../redux/reducers/profileReducer";
import { useDispatch } from "react-redux";

const JobOfInterestSelectionPage = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [jobGoals, setJobGoals] = useState([]);
  const [initialJobGoals, setInitialJobGoals] = useState([]);
  const [drawerOpen, setdrawerOpen] = useState(false);
  const [dialog, setDialog] = useState({ open: false });
  const [gridInitialFilter, setGridInitialFilter] = useState(null);
  const dispatch = useDispatch();

  // On step load
  useEffect(() => {
    props.setProgressValue(75);
    if (props.existingData) {
      setJobGoals(props.existingData);
      setInitialJobGoals(props.existingData);
    } 
  }, []);

  useEffect(() => {
    let filterModel = calculateAdvancedQuery(jobGoals);
    setGridInitialFilter(filterModel);
  }, [jobGoals.length]);

  const onStepClose = async (goStepBack = false, stepCompleted = true) => {
    // All the job goals that are deleted or added
    let updatedJobGoals = [];
    // Check for job goals that user deleted
    initialJobGoals.forEach((jobGoal) => {
      if (!jobGoals.find((x) => x.id === jobGoal.id)) {
        updatedJobGoals.push({
          id: jobGoal.id,
          isDeleted: true,
        });
      }
    });
    // Check for new job goals that user added
    jobGoals.forEach((jobGoal) => {
      if (!initialJobGoals.find((x) => x.id === jobGoal.id)) {
        updatedJobGoals.push({
          jobRoleId: jobGoal.id,
          isNew: true,
        });
      }
    });
    let data = {
      currentStep: 2,
      updatedJobGoals: updatedJobGoals,
      isStepCompleted: stepCompleted,
    };
    // Request to save the user's Job Goals and then navigate to next step or previous step
    await saveStep(data);
    // Save the jobGoals in local state so not do another request to get the data
    dispatch(profileActions.setJobGoals(jobGoals));
    if (stepCompleted) {
      if (goStepBack) props.navigateStepBack();
      else props.navigateStepFront();
    }
  };

  useImperativeHandle(ref, () => ({
    onStepClose,
  }));

  // Add new jobs to the list
  const addNewJobs = (newJobs) => {
    const jobsToAdd = [];
    newJobs.forEach((newJob) => {
      const existingJob = jobGoals.find((job) => job.id === newJob.id);
      if (!existingJob) {
        jobsToAdd.push(newJob);
      }
    });
    setJobGoals((prevJobs) => [...jobsToAdd, ...prevJobs]);
  };

  // Delete job from the list
  const handleDeleteJob = (jobId) => {
    setJobGoals(jobGoals.filter((job) => job.id !== jobId));
  };

  // Grid columns definitions for the Jobs step drawer
  const columnDefs = [
    {
      cellRenderer: "CustomRadioCell",
      maxWidth: 40,
    },
    {
      field: "name",
      filter: true,
      cellRenderer: "CustomNameCell",
      autoHeight: true,
      maxWidth: 550,
    },
    {
      field: "jobFamilyName",
      hide: true,
      selectedFilterColumn: true,
      rowGroup: true,
    },
  ];

  return (
    <>
      {dialog?.open && <DetailsDialogs dialog={dialog} setDialog={setDialog} />}
      <StepDrawer
        open={drawerOpen}
        header={t("profileScreen.drawer.addJobsHeader")}
        searchLabel={t("profileScreen.drawer.searchJobsLabel")}
        filterLabel={t("profileScreen.drawer.searchJobsFilterLabel")}
        onClose={() => setdrawerOpen(false)}
        columnDefs={columnDefs}
        initialGridState={
          gridInitialFilter
            ? { filter: { filterModel: gridInitialFilter } }
            : null
        }
        gridName={GRID_NAMES.JOB_OF_INTEREST_SELECTION}
        datasourceUrl={API_PARAM.AVAILABLE_JOB_GOALS}
        filterValuesUrl={API_PARAM.AVAILABLE_JOB_GOAL_VALUES}
        onSave={addNewJobs}
      />
      <StepContainer
        stepTitle={t("profileScreen.jobOfInterestStep.selection.title")}
        stepDescription={t(
          "profileScreen.jobOfInterestStep.selection.description"
        )}
        onTransparentButtonClick={() => onStepClose(true)}
        transparentButtonLabel={t("profileScreen.btnBack")}
        onSecondaryButtonClick={onStepClose}
        secondaryButtonLabel={t("profileScreen.btnNext")}
        hasLargeHeader
        header={
          <AddButton
            label={t("profileScreen.jobOfInterestStep.btnAddJobs")}
            onClick={() => setdrawerOpen(true)}
          />
        }
      >
        {jobGoals.length > 0 &&
          jobGoals.map((job) => (
            <StepRowCard
              key={job.id}
              title={job.name}
              description={job.disciplineName}
              hasHover
              hasDelete
              onDelete={() => handleDeleteJob(job.id)}
              onInfoClicked={() => setDialog({ open: true, jobId: job.id })}
            />
          ))}
      </StepContainer>
    </>
  );
});

export default JobOfInterestSelectionPage;
